import { isValidObjectId } from "@utils";
import {
  WIZARD_MODES,
  WIZARD_INITIAL_DATA,
  USE_CASES,
  LATEST_TAG,
  RELEASE_NAME_REGEX,
  RELEASE_NAME_MAX_LENGTH,
  SHA_REGEX,
  UUID_V4_REGEX,
  ERROR_MESSAGES,
  DEFAULT_TTL_HOURS,
  NLU_MODES,
  VALID_CONFIG_ID_VALUES,
  FIELD_IDS,
  SPLIT_DRAFT_PREVIEW_MODES,
  getServicesForUseCase,
} from "./DeployWizard.consts";

export const validateReleaseName = (value, existingNames, isEdit) => {
  if (!value) return ERROR_MESSAGES.REQUIRED_FIELD;

  const formatted = value.replace(/ /g, "-");
  if (!RELEASE_NAME_REGEX.test(formatted)) {
    return ERROR_MESSAGES.INVALID_RELEASE_NAME;
  }
  if (formatted.length > RELEASE_NAME_MAX_LENGTH) {
    return ERROR_MESSAGES.RELEASE_NAME_TOO_LONG;
  }
  if (!isEdit && existingNames.includes(formatted)) {
    return ERROR_MESSAGES.RELEASE_NAME_EXISTS;
  }
  return "";
};

export const validateShaField = (value, allowLatest = true) => {
  if (!value || value.trim() === "") return "";
  if (allowLatest && value === LATEST_TAG) return "";
  if (!SHA_REGEX.test(value)) return ERROR_MESSAGES.INVALID_SHA;
  return "";
};

export const validateAssistantId = (value) => {
  if (!value || value.trim() === "") return "";
  if (!UUID_V4_REGEX.test(value.trim())) return ERROR_MESSAGES.INVALID_ASSISTANT_ID;
  return "";
};

export const validateConfigId = (value) => {
  if (!value || value.trim() === "") return "";
  const trimmed = value.trim();
  if (trimmed === VALID_CONFIG_ID_VALUES.ACTIVE || trimmed === VALID_CONFIG_ID_VALUES.DRAFT || isValidObjectId(trimmed)) {
    return "";
  }
  return ERROR_MESSAGES.INVALID_CONFIG_ID;
};

export const validateMemory = (value) => {
  if (!value || (typeof value === "string" && value.trim() === "")) return "";
  const num = Number(value);
  if (isNaN(num) || num < 1 || num > 10) return ERROR_MESSAGES.MEMORY_OUT_OF_RANGE;
  return "";
};

export const validatePrefilledFormData = (formData, useCaseId, existingNames, isEdit) => {
  const { required, optional } = getServicesForUseCase(useCaseId);
  const errors = {};
  const setError = (fieldId, error) => {
    if (error) errors[fieldId] = error;
  };

  if (formData[FIELD_IDS.RELEASE_NAME]) {
    setError(FIELD_IDS.RELEASE_NAME, validateReleaseName(formData[FIELD_IDS.RELEASE_NAME], existingNames, Boolean(isEdit)));
  }
  [...required, ...optional].forEach(({ id, showLatestToggle }) => {
    setError(id, validateShaField(formData[id], showLatestToggle));
  });
  setError(FIELD_IDS.ASSISTANT_ID, validateAssistantId(formData[FIELD_IDS.ASSISTANT_ID]));
  setError(FIELD_IDS.CONFIG_ID, validateConfigId(formData[FIELD_IDS.CONFIG_ID]));
  setError(FIELD_IDS.MEMORY, validateMemory(formData[FIELD_IDS.MEMORY]));
  return errors;
};

export const getInitialLatestToggles = (formData, useCaseId) => {
  const { required, optional } = getServicesForUseCase(useCaseId);
  const toggles = {};
  [...required, ...optional].forEach(({ id, showLatestToggle }) => {
    if (showLatestToggle && formData[id] === LATEST_TAG) {
      toggles[id] = true;
    }
  });
  return toggles;
};

export const getInitialErrors = ({ mode, formData, useCaseId, existingNames, hasRestoredData }) => {
  if (hasRestoredData) return validatePrefilledFormData(formData, useCaseId, existingNames, false);
  if (mode !== WIZARD_MODES.EDIT && mode !== WIZARD_MODES.REACTIVATE) return {};

  const { [FIELD_IDS.RELEASE_NAME]: _lockedReleaseName, ...errors } = validatePrefilledFormData(formData, useCaseId, existingNames, true);
  return errors;
};

export const getInitialStep = (mode, hasRestoredData, hasErrors) => {
  if (mode === WIZARD_MODES.VIEW) return 2;
  if (mode === WIZARD_MODES.REACTIVATE) return hasErrors ? 1 : 2;
  if (mode === WIZARD_MODES.EDIT || hasRestoredData) return 1;
  return 0;
};

export const normalizeShaInput = (value) => (value ?? "").trim().toLowerCase();

export const getInitialUseCase = (mode, initialData) => {
  const isEdit = mode === WIZARD_MODES.EDIT;
  const isReactivate = mode === WIZARD_MODES.REACTIVATE;
  const isView = mode === WIZARD_MODES.VIEW;

  if (isEdit || isReactivate || isView) {
    return initialData?.useCase && USE_CASES[initialData.useCase]
      ? initialData.useCase
      : USE_CASES.ADVANCED.id;
  }
  return initialData?.useCase || null;
};

export const getInitialFormData = (mode, initialData) => {
  const isEdit = mode === WIZARD_MODES.EDIT;
  const isReactivate = mode === WIZARD_MODES.REACTIVATE;
  const isView = mode === WIZARD_MODES.VIEW;

  if (isEdit || isReactivate || isView) {
    const data = { ...WIZARD_INITIAL_DATA };
    Object.keys(data).forEach((key) => {
      if (initialData[key] !== undefined) {
        data[key] = initialData[key];
      }
    });
    if (isReactivate) {
      data.ttlHours = DEFAULT_TTL_HOURS;
    }
    return data;
  }
  return { ...WIZARD_INITIAL_DATA, ...initialData };
};

export const buildPayload = (formData, selectedUseCase) => {
  const payload = { ...formData };

  const useCase = USE_CASES[selectedUseCase];
  const effectiveNluMode = payload[FIELD_IDS.NLU_MODE] || useCase?.nluMode;
  if (effectiveNluMode === NLU_MODES.DEDICATED) {
    payload.nluRuntimeModes = [NLU_MODES.DEDICATED];
  } else if (effectiveNluMode === NLU_MODES.API && payload[FIELD_IDS.NLU_RUNTIME_TAG]) {
    payload.nluRuntimeModes = [NLU_MODES.API];
  }
  delete payload[FIELD_IDS.NLU_MODE];

  const hasWidgetServerTag = Boolean(payload.widgetServerTag && payload.widgetServerTag !== "");
  if (useCase?.splitDraftPreview === SPLIT_DRAFT_PREVIEW_MODES.ALWAYS
    || (useCase?.splitDraftPreview === SPLIT_DRAFT_PREVIEW_MODES.WITH_WIDGET_SERVER && hasWidgetServerTag)) {
    payload.splitDraftPreview = true;
  }

  Object.keys(payload).forEach((key) => {
    const val = payload[key];
    if (val === "" || val === undefined || val === false || (Array.isArray(val) && val.length === 0)) {
      delete payload[key];
    }
  });

  delete payload[FIELD_IDS.USE_CASE];
  payload.useCase = selectedUseCase;
  return payload;
};
